import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../hooks/useCart';
import { useUserContext } from '../hooks/useUserContext';
import {
  getIndividualPrices,
  getMarketplaceBundles,
  type Bundle,
  type BundleItem,
  type IndividualPrice,
} from '../server/api';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.length === 3 ? currency.toUpperCase() : 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

function humanAssetLabel(type: string, qty: number): string {
  const n = qty;
  const labels: Record<string, [string, string]> = {
    email: ['email', 'emails'],
    domain: ['domain', 'domains'],
    laptop: ['laptop', 'laptops'],
    phone: ['phone', 'phones'],
    server: ['server', 'servers'],
    cloud_account: ['cloud account', 'cloud accounts'],
  };
  const pair = labels[type] ?? ['item', 'items'];
  return `${n} ${n === 1 ? pair[0] : pair[1]}`;
}

function formatBundleItemsLine(items: BundleItem[]): string {
  return items.map((i) => humanAssetLabel(i.asset_type, i.quantity)).join(', ');
}

const SERVICE_COPY: Record<
  string,
  { title: string; icon: string; description: string; maxQty: number }
> = {
  email: {
    title: 'Email',
    icon: '📧',
    description: 'Monitor Gmail or Microsoft 365 for phishing, fraud, and account takeover.',
    maxQty: 10,
  },
  domain: {
    title: 'Domain',
    icon: '🌐',
    description: 'Watch your domains on global threat feeds and breach databases.',
    maxQty: 10,
  },
  laptop: {
    title: 'Device (laptop)',
    icon: '💻',
    description: 'Real-time endpoint monitoring with the ShieldSME security agent.',
    maxQty: 50,
  },
  phone: {
    title: 'Phone',
    icon: '📱',
    description: 'Extend monitoring to mobile endpoints (where available).',
    maxQty: 50,
  },
  server: {
    title: 'Server',
    icon: '🖥️',
    description: 'Server workload visibility and threat detection.',
    maxQty: 50,
  },
  cloud_account: {
    title: 'Cloud account',
    icon: '☁️',
    description: 'Cloud posture signals and configuration risk (coming soon).',
    maxQty: 50,
  },
};

function BundleCardSkeleton() {
  return (
    <div className="animate-pulse rounded-2xl border border-border bg-surface/50 p-5">
      <div className="h-5 w-2/3 rounded bg-foreground/10" />
      <div className="mt-2 h-4 w-full rounded bg-foreground/10" />
      <div className="mt-4 h-8 w-1/2 rounded bg-foreground/10" />
    </div>
  );
}

type MarketplaceErrorBoundaryState = { hasError: boolean };

class MarketplaceErrorBoundary extends React.Component<
  React.PropsWithChildren,
  MarketplaceErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): MarketplaceErrorBoundaryState {
    return { hasError: true };
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="mx-auto max-w-lg rounded-xl border border-danger/30 bg-danger/5 p-8 text-center">
          <p className="text-sm text-danger">Something went wrong loading the marketplace.</p>
          <button
            type="button"
            className="mt-4 text-sm font-semibold text-accent hover:underline"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function MarketplacePageContent() {
  const navigate = useNavigate();
  const { isPersonal, profileLoaded, countryCode, isAfrican } = useUserContext();
  const {
    items,
    addBundle,
    addIndividual,
    removeItem,
    updateQuantity,
    subtotal,
    currency: cartCurrency,
    totalItems,
  } = useCart();

  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [prices, setPrices] = useState<IndividualPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [qty, setQty] = useState<Record<string, number>>({
    email: 1,
    domain: 1,
    laptop: 1,
    phone: 1,
    server: 1,
    cloud_account: 1,
  });

  const effectiveCountry = useMemo(() => {
    if (profileLoaded && countryCode && /^[A-Z]{2}$/.test(countryCode)) return countryCode;
    return 'US';
  }, [profileLoaded, countryCode]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const cc = effectiveCountry;
        const [b, p] = await Promise.all([getMarketplaceBundles(cc), getIndividualPrices(cc)]);
        if (cancelled) return;
        setBundles(b);
        if (p.length > 0) {
          setPrices(p);
        } else {
          const fb = await getIndividualPrices('US');
          if (cancelled) return;
          setPrices(fb);
        }
        if (b.length === 0 && p.length === 0) {
          setError('Could not load marketplace. Check your connection and try again.');
        }
      } catch {
        if (!cancelled) {
          setBundles([]);
          setPrices([]);
          setError('Could not load marketplace. Check your connection and try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [effectiveCountry, reloadKey]);

  const maxBundleDiscount = useMemo(() => {
    if (bundles.length === 0) return 25;
    return Math.max(...bundles.map((x) => x.discount_percent));
  }, [bundles]);

  const priceByType = useMemo(() => {
    const m = new Map<string, IndividualPrice>();
    for (const r of prices) m.set(r.asset_type, r);
    return m;
  }, [prices]);

  const subtitle = isPersonal
    ? 'Protect what matters — add services as you grow'
    : 'Protect your business — add services as you grow';

  const purchasableTypes = useMemo(() => {
    const order = ['email', 'domain', 'laptop', 'phone', 'server', 'cloud_account'];
    return order.filter((t) => priceByType.has(t));
  }, [priceByType]);

  const bumpQty = (assetType: string, delta: number) => {
    const meta = SERVICE_COPY[assetType];
    const max = meta?.maxQty ?? 50;
    setQty((prev) => {
      const cur = prev[assetType] ?? 1;
      const next = Math.min(max, Math.max(1, cur + delta));
      return { ...prev, [assetType]: next };
    });
  };

  const bundleInCart = (id: string) => items.some((i) => i.type === 'bundle' && i.bundle_id === id);

  const cartPanel = (
    <div className="flex flex-col rounded-2xl border border-border bg-surface/70 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Your cart</h2>
        {totalItems > 0 ? (
          <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
            {totalItems} item{totalItems === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-muted">Your cart is empty</p>
      ) : (
        <ul className="mt-3 max-h-64 space-y-3 overflow-y-auto">
          {items.map((line) => (
            <li key={line.id} className="flex flex-col gap-1 border-b border-border/60 pb-3 text-sm last:border-0">
              <div className="flex items-start justify-between gap-2">
                <span className="font-medium text-foreground">{line.name}</span>
                <button
                  type="button"
                  onClick={() => removeItem(line.id)}
                  className="shrink-0 text-xs font-semibold text-danger hover:underline"
                >
                  Remove
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <span>Qty</span>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-0.5 font-semibold"
                  onClick={() => updateQuantity(line.id, line.quantity - 1)}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span className="tabular-nums">{line.quantity}</span>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-0.5 font-semibold"
                  onClick={() => updateQuantity(line.id, line.quantity + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-muted">
                {formatMoney(line.unit_price * line.quantity, line.currency)} —{' '}
                <span className="italic">estimate</span>
              </p>
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 ? (
        <>
          <p className="mt-3 text-sm font-semibold text-foreground">
            Subtotal <span className="text-muted">(estimate)</span>: {formatMoney(subtotal, cartCurrency)}
          </p>
          <p className="mt-2 text-xs text-muted">
            Prices are estimates. Final price confirmed before payment.
          </p>
        </>
      ) : (
        <p className="mt-3 text-xs text-muted">Add a bundle or service above to get started.</p>
      )}
      <button
        type="button"
        disabled={items.length === 0}
        onClick={() => {
          setMobileCartOpen(false);
          navigate('/checkout');
        }}
        className="mt-4 w-full rounded-lg bg-accent py-2.5 text-sm font-semibold text-cta-text transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Proceed to checkout →
      </button>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl pb-28 lg:pb-10">
      <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Marketplace</h1>
          <p className="mt-1 text-sm text-muted sm:text-base">{subtitle}</p>
          <p className="mt-1 text-xs text-muted">
            Showing prices for <span className="font-medium text-foreground">{effectiveCountry}</span>
          </p>
        </div>
        <div className="flex items-center justify-end gap-3">
          <Link
            to="/checkout"
            className="relative inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-foreground/5"
          >
            <span aria-hidden>🛒</span>
            <span className="hidden sm:inline">Cart</span>
            {totalItems > 0 ? (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-white">
                {totalItems > 99 ? '99+' : totalItems}
              </span>
            ) : null}
          </Link>
        </div>
      </header>

      {error ? (
        <div className="mb-6 rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
          {error}{' '}
          <button type="button" onClick={() => setReloadKey((k) => k + 1)} className="font-semibold underline">
            Retry
          </button>
        </div>
      ) : null}

      <div className="lg:grid lg:grid-cols-[1fr_320px] lg:items-start lg:gap-8">
        <div className="space-y-12">
          <section aria-labelledby="bundles-heading">
            <h2 id="bundles-heading" className="text-xl font-semibold text-foreground">
              Bundles — Save up to {Math.round(maxBundleDiscount)}%
            </h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {loading
                ? [0, 1, 2].map((k) => <BundleCardSkeleton key={k} />)
                : bundles.map((b) => {
                    const inCart = bundleInCart(b.id);
                    const expanded = expandedId === b.id;
                    return (
                      <article
                        key={b.id}
                        className={`flex flex-col rounded-2xl border bg-surface/60 p-5 ${
                          b.is_featured ? 'border-accent/50 ring-1 ring-accent/20' : 'border-border'
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          {b.is_featured ? (
                            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
                              Featured
                            </span>
                          ) : null}
                          <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-xs font-semibold text-muted">
                            {b.discount_percent}% off
                          </span>
                        </div>
                        <h3 className="mt-2 text-lg font-semibold text-foreground">{b.name}</h3>
                        <p className="mt-1 text-sm text-muted">{b.tagline}</p>
                        <p className="mt-2 text-xs text-muted">{b.target_audience}</p>
                        <p className="mt-3 text-sm text-foreground">{formatBundleItemsLine(b.items)}</p>
                        <div className="mt-3 flex flex-wrap items-baseline gap-2">
                          <span className="text-sm text-muted line-through">
                            {formatMoney(b.base_price, b.currency)}/mo
                          </span>
                          <span className="text-2xl font-bold text-accent">
                            {formatMoney(b.discounted_price, b.currency)}
                            <span className="text-sm font-medium text-muted">/mo</span>
                          </span>
                        </div>
                        <p className="mt-1 text-xs font-semibold text-accent">
                          Save {formatMoney(b.savings, b.currency)}/mo
                        </p>
                        <button
                          type="button"
                          onClick={() => setExpandedId(expanded ? null : b.id)}
                          className="mt-3 text-left text-xs font-semibold text-accent hover:underline"
                          aria-expanded={expanded}
                        >
                          {expanded ? 'Hide description' : 'Show description'}
                        </button>
                        {expanded ? (
                          <p className="mt-2 text-sm leading-relaxed text-muted">{b.description}</p>
                        ) : null}
                        <button
                          type="button"
                          disabled={inCart}
                          onClick={() => addBundle(b)}
                          className={`mt-4 w-full rounded-lg py-2.5 text-sm font-semibold transition ${
                            inCart
                              ? 'cursor-default bg-accent/20 text-accent'
                              : 'bg-accent text-white hover:opacity-90'
                          }`}
                        >
                          {inCart ? '✓ In cart' : 'Add to cart'}
                        </button>
                      </article>
                    );
                  })}
            </div>
          </section>

          <section aria-labelledby="individual-heading">
            <h2 id="individual-heading" className="text-xl font-semibold text-foreground">
              Individual Services
            </h2>
            <p className="mt-1 text-sm text-muted">Add exactly what you need</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {purchasableTypes.map((t) => {
                const row = priceByType.get(t)!;
                const meta = SERVICE_COPY[t] ?? {
                  title: t,
                  icon: '📦',
                  description: 'Monitored asset billing.',
                  maxQty: 50,
                };
                const amount = row.amount;
                const cur = row.currency;
                const n = qty[t] ?? 1;
                return (
                  <div key={t} className="rounded-2xl border border-border bg-surface/60 p-5">
                    <p className="text-2xl" aria-hidden>
                      {meta.icon}
                    </p>
                    <h3 className="mt-2 font-semibold text-foreground">{meta.title}</h3>
                    <p className="mt-1 text-sm text-muted">{meta.description}</p>
                    <p className="mt-3 text-lg font-bold text-foreground">
                      {formatMoney(amount, cur)}
                      <span className="text-sm font-medium text-muted">/mo per {meta.title.toLowerCase()}</span>
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-sm"
                        onClick={() => bumpQty(t, -1)}
                      >
                        −
                      </button>
                      <span className="w-8 text-center tabular-nums">{n}</span>
                      <button
                        type="button"
                        className="rounded border border-border px-2 py-1 text-sm"
                        onClick={() => bumpQty(t, 1)}
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        addIndividual(t, meta.title, n, row.amount, row.currency);
                      }}
                      className="mt-4 w-full rounded-lg border border-accent/40 bg-accent/10 py-2 text-sm font-semibold text-accent hover:bg-accent/20"
                    >
                      Add to cart
                    </button>
                  </div>
                );
              })}

              {isAfrican ? (
                <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5">
                  <p className="text-2xl" aria-hidden>
                    📲
                  </p>
                  <h3 className="mt-2 font-semibold text-foreground">MoMo Protection</h3>
                  <p className="mt-1 text-sm text-muted">Instant checks on suspicious mobile-money messages.</p>
                  <p className="mt-3 text-lg font-bold text-accent">FREE — Always included</p>
                  <p className="mt-4 text-sm font-semibold text-accent">✓ Included with your account</p>
                </div>
              ) : null}
            </div>
          </section>
        </div>

        <aside className="mt-10 hidden lg:sticky lg:top-24 lg:mt-0 lg:block">{cartPanel}</aside>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-surface/95 p-3 backdrop-blur-md lg:hidden">
        <button
          type="button"
          onClick={() => setMobileCartOpen((o) => !o)}
          className="flex w-full items-center justify-between rounded-lg border border-border px-4 py-3 text-sm font-semibold text-foreground"
        >
          <span>🛒 Cart {totalItems > 0 ? `(${totalItems})` : ''}</span>
          <span>{mobileCartOpen ? '▾' : '▴'}</span>
        </button>
        {mobileCartOpen ? <div className="mt-3 max-h-[60vh] overflow-y-auto">{cartPanel}</div> : null}
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  return (
    <MarketplaceErrorBoundary>
      <MarketplacePageContent />
    </MarketplaceErrorBoundary>
  );
}
